import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import { sanityReadClient } from '../../sanity/client';
import { ToastContext, ToastTypes } from '../../common/contexts/toast.context';

interface SanityPost {
  _id: string;
  title: string;
  slug: { current: string };
  author?: string;
  publishDate?: string;
  summary?: string;
}

const BlogList = () => {
  const { createToast } = useContext(ToastContext);
  const [posts, setPosts] = useState<SanityPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await sanityReadClient.fetch<SanityPost[]>(
        `*[_type == "post"] | order(publishDate desc) {
          _id, title, slug, author, publishDate, summary
        }`
      );
      setPosts(data);
    } catch (err) {
      createToast({
        title: 'Error',
        description: <>Failed to load blog posts from Sanity.</>,
        type: ToastTypes.Fail,
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Header
        title="Blog"
        className="pb-6"
        rightSide={
          <Link to="/blog/new">
            <span className="bg-primary-400 text-white rounded-md inline-flex justify-center py-2 pl-4 pr-2 font-medium hover:bg-primary-600">
              New article
            </span>
          </Link>
        }
      />
      {loading ? (
        <Loading />
      ) : posts.length === 0 ? (
        <div className="p-6 text-gray-500 text-center">No blog posts yet. Create your first article!</div>
      ) : (
        <div>
          {posts.map((post, i) => (
            <Link
              to={`/blog/${post._id}`}
              key={post._id}
              className="cursor-pointer"
            >
              <div
                className={[
                  'p-6 relative pl-4 pr-6 py-5 sm:py-6 sm:pl-6 hover:bg-gray-200',
                  i % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                ].join(' ')}
              >
                <div className="flex flex-col gap-1">
                  {post.publishDate && (
                    <span className="font-semibold text-sm text-gray-500">
                      {new Date(post.publishDate).toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                  <span className="font-bold text-gray-900">{post.title}</span>
                  {post.summary && (
                    <span className="italic text-gray-600 text-sm">{post.summary}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
};

export default BlogList;
